/**
 * VendaPdvDesktopService
 *
 * Responsável por finalizar vendas no modo PDV Desktop (Electron).
 *
 * Regras de negócio:
 *  1. Lê configurações locais do terminal.
 *  2. Gera uuid_venda_terminal único.
 *  3. Preenche série, número, numeroNota, id_terminal_venda, origem_venda,
 *     sync_app e isPDVSync.
 *  4. Se houver internet, tenta enviar imediatamente para
 *     POST /api/offline/vendas/sincronizar.
 *     - Sucesso → salva localmente como "sincronizada".
 *     - Falha   → salva localmente como "pendente".
 *  5. Se não houver internet → salva localmente como "pendente".
 *  6. Incrementa proxima_venda SOMENTE após salvar/sincronizar com sucesso.
 */
import { getElectronAPI } from "@/plugins/desktop";
import { SYSTEM_VARIABLE }  from "@/modules/Application";
import TerminalConfigService from "./TerminalConfigService";
import AppConfigRepository from "@/modules/PdvDesktop/repositories/AppConfigRepository";

function _getBaseUrl() {
  return (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");
}

const VendaPdvDesktopService = {
  /**
   * Finaliza uma venda no contexto Desktop/Electron.
   *
   * @param {object} dados      - payload da venda (mesmo objeto usado no Form.vue)
   * @param {object} [callbacks]
   * @param {function} [callbacks.onSynced]  - chamado quando a venda foi sincronizada imediatamente
   * @param {function} [callbacks.onPending] - chamado quando a venda foi salva como pendente
   * @returns {Promise<{ uuid_venda_terminal: string, synced: boolean }>}
   */
  async finalizarVenda(dados, callbacks = {}) {
    const api = getElectronAPI();
    if (!api) throw new Error("Electron API não disponível.");

    // Valida vínculo do terminal (fail-open se offline — venda prossegue localmente)
    await TerminalConfigService.garantirTerminalAutorizado();

    // ── 1. Lê configurações do terminal ────────────────────────────────────
    const [
      id_terminal_venda,
      uuid_terminal,
      id_empresa,
      id_configuracao,
      serie_nfe,
      proxima_venda_str,
      api_token,
      device_id,
    ] = await Promise.all([
      api.config.get("id_terminal_venda"),
      api.config.get("uuid_terminal"),
      api.config.get("id_empresa"),
      api.config.get("id_configuracao"),
      api.config.get("serie_nfe"),
      api.config.get("proxima_venda"),
      api.config.get("api_token"),
      AppConfigRepository.getDeviceId(),
    ]);

    if (!uuid_terminal || !device_id) {
      throw new Error("Terminal não configurado corretamente. Configure novamente o terminal.");
    }

    const proximaVenda = parseInt(proxima_venda_str || "1", 10);
    const agora        = new Date().toISOString();
    const buyer        = dados.buyer || {};

    // ── 2. Gera identificador único ────────────────────────────────────────
    const uuid_venda_terminal = crypto.randomUUID();

    // ── 3. Monta registro local ────────────────────────────────────────────
    const vendaLocal = {
      uuid_venda_terminal,
      id_terminal_venda:    id_terminal_venda ? parseInt(id_terminal_venda, 10) : null,
      id_empresa:           id_empresa        ? parseInt(id_empresa, 10)        : null,
      identificacaoCliente: buyer.cnpj_cpf    ?? buyer.cpf ?? null,
      nomeCliente:          buyer.nome_fantasia ?? buyer.nome ?? null,
      numero:               proximaVenda,
      numeroNota:           proximaVenda,
      modelo:               dados.modelo      ?? 65,
      serie:                serie_nfe         ?? dados.serie ?? null,
      finalidade:           dados.finalidade  ?? 1,
      tipoNF:               dados.tpNF        ?? 1,
      valorTotal:           Number(dados.price ?? 0),
      dtVenda:              agora,
      emissao:              agora,
      status:               1,
      statusTransmissao:    "OFFLINE",
      origem_venda:         "pdv_offline",
      isPDVSync:            1,
      sync_app:             1,
      status_sync:          "pendente",
      id_vendedor:          dados.id_vendedor ?? null,
      dados_json:           JSON.stringify(dados),
      payload_json:         JSON.stringify({
        dados,
        uuid_terminal,
        id_configuracao,
        id_empresa,
        serie: serie_nfe ?? dados.serie,
        numero: proximaVenda,
      }),
      itens: (dados.productOfTradeCollection || []).map((item) => ({
        id_produto:     item.id_produto     ?? item.product?.id          ?? null,
        codigo:         item.codigo         ?? item.product?.codigo      ?? null,
        descricao:      item.descricao      ?? item.product?.descricao   ?? item.name ?? "",
        cod_barras:     item.cod_barras     ?? item.product?.cod_barras  ?? null,
        quantidade:     Number(item.quantidade  ?? item.qty   ?? 1),
        valor_unitario: Number(item.valor_unitario ?? item.price ?? 0),
        desconto:       Number(item.desconto   ?? item.discount ?? 0),
        acrescimo:      Number(item.acrescimo  ?? 0),
        total:          Number(item.total      ?? item.totalPrice ?? 0),
      })),
      pagamentos: (dados.paymentModeCollection || []).map((pag) => ({
        id_meio_pagamento: pag.id_meio_pagamento ?? null,
        descricao:         pag.descricao         ?? null,
        valor:             Number(pag.value       ?? 0),
        parcelas:          pag.vezes             ?? 1,
        vencimento:        (pag.range && pag.range[0]) ? pag.range[0].date : null,
      })),
    };

    // ── 4. Tenta sincronizar imediatamente se estiver online ───────────────
    let synced = false;

    if (navigator.onLine && api_token) {
      try {
        const baseUrl = _getBaseUrl();
        const resp = await fetch(`${baseUrl}/api/offline/vendas/sincronizar`, {
          method: "POST",
          headers: {
            Authorization:  `Bearer ${api_token}`,
            "Content-Type": "application/json",
            Accept:         "application/json",
          },
          body: JSON.stringify({
            uuid_terminal,
            device_id,
            vendas: [{
              uuid_venda_terminal,
              numero:               proximaVenda,
              numeroNota:           proximaVenda,
              serie:                serie_nfe ?? dados.serie ?? null,
              modelo:               dados.modelo   ?? 65,
              finalidade:           dados.finalidade ?? 1,
              tipoNF:               dados.tpNF      ?? 1,
              valorTotal:           vendaLocal.valorTotal,
              dtVenda:              agora,
              emissao:              agora,
              identificacaoCliente: vendaLocal.identificacaoCliente,
              nomeCliente:          vendaLocal.nomeCliente,
              origem_venda:         "pdv_offline",
              isPDVSync:            true,
              sync_app:             true,
              id_terminal_venda:    vendaLocal.id_terminal_venda,
              id_vendedor:          vendaLocal.id_vendedor,
              observation:          dados.observation ?? dados.observacao ?? "",
              observacao:           dados.observation ?? dados.observacao ?? "",
              dados:                dados,
            }],
          }),
        });

        if (resp.ok) {
          const json       = await resp.json();
          const resultados = json.resultados ?? json.data ?? [];
          const resultado  = resultados.find(
            (r) => r.uuid_venda_terminal === uuid_venda_terminal
          );
          if (
            resultado &&
            (resultado.status === "sincronizada" || resultado.status === "ja_sincronizada")
          ) {
            synced = true;
          }
        }
      } catch (e) {
        console.warn("[VendaPdvDesktopService] Falha ao sincronizar imediatamente:", e.message);
      }
    }

    // ── 5. Salva localmente com status correto ─────────────────────────────
    if (synced) {
      vendaLocal.status_sync    = "sincronizada";
      vendaLocal.sincronizada_em = agora;
    }

    await api.vendas.salvar(vendaLocal);

    // ── 6. Se pendente, enfileira para próxima sincronização ───────────────
    if (!synced) {
      await api.sync.enqueue({
        tipo:            "venda",
        uuid_referencia: uuid_venda_terminal,
        payload:         { uuid_venda_terminal },
      });
    }

    // ── 7. Incrementa proxima_venda APÓS salvar com sucesso ────────────────
    await api.config.set("proxima_venda", String(proximaVenda + 1));

    // ── 8. Dispara callbacks ──────────────────────────────────────────────
    if (synced && typeof callbacks.onSynced === "function") {
      callbacks.onSynced();
    } else if (!synced && typeof callbacks.onPending === "function") {
      callbacks.onPending();
    }

    return { uuid_venda_terminal, synced };
  },
};

export default VendaPdvDesktopService;
